import { DragDropContext, DragStart, DropResult } from '@hello-pangea/dnd';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { stringify as stringifyQuery } from 'query-string';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import matchingRoundsAPI, {
  AdvancedMatchPage,
  MatchFilterParams,
  MatchingRoundId,
  MatchingRoundStatus,
  MatchRecommendation,
  ReverseMatchRecommendation,
  ReverseMatchVenture,
  SORT_BY_MAP,
  SORT_FILTERING_MODE_MAP,
} from '../../api/matching-rounds';
import { MentorId } from '../../api/mentors';
import { TenantId } from '../../api/tenants/Tenant';
import { VentureId } from '../../api/ventures/types/Venture';
import { SnackMessage } from '../../components/common';
import { ConfirmDialog } from '../../components/common/confirm-dialog';
import HeatmapAssignMentorModal from '../../components/matching-rounds/heatmap/heatmap-assign-mentor-modal';
import HeatmapAssignVentureModal from '../../components/matching-rounds/heatmap/heatmap-assign-venture-modal';
import HeatmapCard from '../../components/matching-rounds/heatmap/heatmap-card';
import { ChipData } from '../../components/matching-rounds/heatmap/heatmap-chip';
import HeatmapHeader, {
  SortOption,
} from '../../components/matching-rounds/heatmap/heatmap-header';
import HeatmapSidebar from '../../components/matching-rounds/heatmap/heatmap-sidebar';
import { HeatmapView } from '../../components/matching-rounds/heatmap/heatmap-tabs';
import HeatmapTooltip, {
  TooltipData,
} from '../../components/matching-rounds/heatmap/heatmap-tooltip';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { Pages, getRoutePath } from '../../router/constants';
import { isMobile } from '../../utils/functions';

/** Valid view values as stored in the URL */
const VALID_VIEWS: HeatmapView[] = ['venture-mentor', 'mentor-venture'];
/** Valid sort values as stored in the URL */
const VALID_SORTS: SortOption[] = [
  'name',
  'assignedCount',
  'unassignedVentures',
  'overloadedMentors',
];

function parseHeatmapQuery(search: string): {
  view: HeatmapView;
  sort: SortOption;
  ventureSearch: string;
  mentorSearch: string;
} {
  const params = new URLSearchParams(search);
  const rawView = params.get('view') ?? '';
  const rawSort = params.get('sort') ?? '';
  return {
    view: (VALID_VIEWS.includes(rawView as HeatmapView)
      ? rawView
      : 'venture-mentor') as HeatmapView,
    sort: (VALID_SORTS.includes(rawSort as SortOption)
      ? rawSort
      : 'name') as SortOption,
    ventureSearch: params.get('vs') ?? '',
    mentorSearch: params.get('ms') ?? '',
  };
}

interface MatchingRoundHeatmapPageProps {
  roundId: MatchingRoundId;
  tenantId?: TenantId;
  roundStatus?: MatchingRoundStatus;
  refreshKey?: number;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 40,
    flexGrow: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  mobileMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 60,
  },
  mainArea: {
    display: 'flex',
    flexGrow: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 12,
    overflow: 'hidden',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    padding: '0 0 12px 0',
  },
  loader: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  listLoader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    minHeight: 300,
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  retryButton: {
    marginTop: 12,
  },
});

function recToChip(rec: MatchRecommendation, locked?: boolean): ChipData {
  return {
    chipId: rec.id,
    name: rec.mentorName || '',
    score: Math.max(rec.score ?? 0, 0),
    assigned: rec.assigned,
    locked,
    tooltipData: {
      type: 'mentor',
      name: rec.mentorName || '',
      entityId: rec.mentorId,
      fields: [
        { label: 'Specialization', value: rec.specialization || '' },
        { label: 'About', value: rec.about || '' },
      ],
    },
  };
}

function ventureToChip(
  mentorId: string,
  v: ReverseMatchVenture,
  assigned: boolean,
  locked?: boolean,
): ChipData {
  return {
    chipId: `${mentorId}-${v.ventureId}`,
    recommendationId: v.recommendationId,
    name: v.ventureName || '',
    score: Math.max(v.score ?? 0, 0),
    assigned,
    locked,
    tooltipData: {
      type: 'venture',
      name: v.ventureName || '',
      entityId: v.ventureId,
      fields: [
        { label: 'Stage', value: v.stage || '' },
        { label: 'Description', value: v.description || '' },
      ],
    },
  };
}

/** Helper to check if a recommendation is approved by both sides */
function isApproved(rec: {
  confirmedByFounder?: boolean;
  confirmedByMentor?: boolean;
}): boolean {
  return !!rec.confirmedByFounder && !!rec.confirmedByMentor;
}

const MatchingRoundHeatmapPage = memo(function MatchingRoundHeatmapPage({
  roundId,
  tenantId,
  refreshKey,
}: MatchingRoundHeatmapPageProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { rb } = useResourceBundles();
  const history = useHistory();
  const location = useLocation();

  // Parse initial values from URL — only on first mount
  const initialQuery = useMemo(
    () => parseHeatmapQuery(location.search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // View state
  const [activeView, setActiveView] = useState<HeatmapView>(initialQuery.view);

  // Venture data (paginated)
  const [venturePages, setVenturePages] = useState<AdvancedMatchPage[]>([]);

  // Mentor data (paginated)
  const [mentorPages, setMentorPages] = useState<ReverseMatchRecommendation[]>(
    [],
  );

  // Shared pagination state — serves whichever view is active, reset on switch
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Search, sort & filters
  const [ventureSearch, setVentureSearch] = useState(
    initialQuery.ventureSearch,
  );
  const [mentorSearch, setMentorSearch] = useState(initialQuery.mentorSearch);
  const [sortBy, setSortBy] = useState<SortOption>(initialQuery.sort);

  // Tooltip
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  // Drag conflict tracking — stores the mentorId or ventureId being dragged
  const [draggingEntityId, setDraggingEntityId] = useState<string | null>(null);

  // Assignment modal state
  const [assignModalVenture, setAssignModalVenture] = useState<{
    ventureId: VentureId;
    ventureName: string;
    matchId: string;
    existingMentorIds: MentorId[];
  } | null>(null);

  const [assignModalMentor, setAssignModalMentor] = useState<{
    mentorId: MentorId;
    mentorName: string;
    existingVentureIds: VentureId[];
  } | null>(null);

  // Remove from round confirmation state
  const [removeFromRoundVenture, setRemoveFromRoundVenture] = useState<{
    ventureId: VentureId;
    ventureName: string;
  } | null>(null);

  const [removeFromRoundMentor, setRemoveFromRoundMentor] = useState<{
    mentorId: MentorId;
    mentorName: string;
  } | null>(null);

  // Vote link copy state
  const [copiedVoteLink, setCopiedVoteLink] = useState<string | null>(null);
  const copiedVoteLinkTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (copiedVoteLink) {
      copiedVoteLinkTimeoutRef.current = setTimeout(() => {
        setCopiedVoteLink(null);
      }, 4000);
    }
    return () => {
      if (copiedVoteLinkTimeoutRef.current) {
        clearTimeout(copiedVoteLinkTimeoutRef.current);
      }
    };
  }, [copiedVoteLink]);

  // Refs for stable callback access (avoids stale closures)
  const isLoadingPageRef = useRef(false);
  const loadGenRef = useRef(0);
  const currentPageRef = useRef(currentPage);
  const ventureDataRef = useRef(venturePages);
  const mentorPagesRef = useRef(mentorPages);
  const activeViewRef = useRef(activeView);

  // Refs for search/sort — stable callback access without stale closures
  const ventureSearchRef = useRef(ventureSearch);
  const mentorSearchRef = useRef(mentorSearch);
  const sortByRef = useRef(sortBy);
  const removeFromRoundRef = useRef(removeFromRoundVenture);
  const removeFromRoundMentorRef = useRef(removeFromRoundMentor);

  // Keep refs in sync
  currentPageRef.current = currentPage;
  ventureDataRef.current = venturePages;
  mentorPagesRef.current = mentorPages;
  activeViewRef.current = activeView;
  ventureSearchRef.current = ventureSearch;
  mentorSearchRef.current = mentorSearch;
  sortByRef.current = sortBy;
  removeFromRoundRef.current = removeFromRoundVenture;
  removeFromRoundMentorRef.current = removeFromRoundMentor;

  const cardListRef = useRef<HTMLDivElement>(null);

  // Build filter params from current ref values
  const buildFilterParams = useCallback((page: number): MatchFilterParams => {
    const currentSort = sortByRef.current;
    const filteringMode = SORT_FILTERING_MODE_MAP[currentSort];
    const params: MatchFilterParams = {
      pageNumber: page,
      pageSize: 100,
      sortBy: SORT_BY_MAP[currentSort] || 'NAME',
    };
    if (filteringMode) {
      params.filteringMode = filteringMode;
    }
    if (ventureSearchRef.current) {
      params.ventureName = ventureSearchRef.current;
    }
    if (mentorSearchRef.current) {
      params.mentorName = mentorSearchRef.current;
    }
    return params;
  }, []);

  // Load venture pages — uses ref guard to avoid stale closure issues
  const loadVenturePage = useCallback(
    async (page: number) => {
      if (isLoadingPageRef.current) return;
      isLoadingPageRef.current = true;
      const gen = loadGenRef.current;
      try {
        setIsLoadingPage(true);
        setLoadError(false);
        const data = await matchingRoundsAPI.filterAdvancedMatches(
          roundId,
          buildFilterParams(page),
        );
        if (gen !== loadGenRef.current) return; // stale — discard
        if (data.length === 0) {
          setHasMorePages(false);
        } else {
          setVenturePages((prev) => [...prev, ...data]);
          setCurrentPage(page);
        }
      } catch (e: unknown) {
        if (gen !== loadGenRef.current) return; // stale — discard
        const msg = (e as AxiosError)?.response?.data?.message;
        setLoadError(true);
        enqueueSnackbar('Failed to load matching data', {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: msg,
            }),
          variant: 'error',
        });
      } finally {
        if (gen === loadGenRef.current) {
          isLoadingPageRef.current = false;
          setIsLoadingPage(false);
          setIsInitialLoading(false);
        }
      }
    },
    [roundId, enqueueSnackbar, buildFilterParams],
  );

  // Load mentor pages — mirrors loadVenturePage pattern
  const loadMentorPage = useCallback(
    async (page: number) => {
      if (isLoadingPageRef.current) return;
      isLoadingPageRef.current = true;
      const gen = loadGenRef.current;
      try {
        setIsLoadingPage(true);
        setLoadError(false);
        const data = await matchingRoundsAPI.filterMentors(
          roundId,
          buildFilterParams(page),
        );
        if (gen !== loadGenRef.current) return; // stale — discard
        if (data.length === 0) {
          setHasMorePages(false);
        } else {
          setMentorPages((prev) => [...prev, ...data]);
          setCurrentPage(page);
        }
      } catch (e: unknown) {
        if (gen !== loadGenRef.current) return; // stale — discard
        const msg = (e as AxiosError)?.response?.data?.message;
        setLoadError(true);
        enqueueSnackbar('Failed to load mentor data', {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: msg,
            }),
          variant: 'error',
        });
      } finally {
        if (gen === loadGenRef.current) {
          isLoadingPageRef.current = false;
          setIsLoadingPage(false);
          setIsInitialLoading(false);
        }
      }
    },
    [roundId, enqueueSnackbar, buildFilterParams],
  );

  // Initial load — respect view from URL
  useEffect(() => {
    if (activeViewRef.current === 'mentor-venture') {
      loadMentorPage(0);
    } else {
      loadVenturePage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadVenturePage, loadMentorPage]);

  // Reload when refreshKey changes (e.g. after simulation)
  const refreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === refreshKeyRef.current) return;
    refreshKeyRef.current = refreshKey;
    loadGenRef.current++;
    isLoadingPageRef.current = false;
    setCurrentPage(0);
    setHasMorePages(true);
    setIsInitialLoading(true);
    setLoadError(false);
    if (activeViewRef.current === 'venture-mentor') {
      setVenturePages([]);
      loadVenturePage(0);
    } else {
      setMentorPages([]);
      loadMentorPage(0);
    }
  }, [refreshKey, loadVenturePage, loadMentorPage]);

  // Infinite scroll — scroll event listener (matches personal-notes pattern)
  // `isInitialLoading` in deps ensures re-run after card list mounts
  useEffect(() => {
    if (isInitialLoading) return;
    const container = cardListRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        hasMorePages &&
        !isLoadingPageRef.current &&
        container.scrollHeight - container.scrollTop <=
          container.clientHeight + 2000
      ) {
        const nextPage = currentPage + 1;
        if (activeViewRef.current === 'venture-mentor') {
          loadVenturePage(nextPage);
        } else {
          loadMentorPage(nextPage);
        }
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [
    hasMorePages,
    currentPage,
    loadVenturePage,
    loadMentorPage,
    isInitialLoading,
  ]);

  // Reload when search or sort changes — skip initial mount (handled above)
  const skipInitialRef = useRef(true);

  useEffect(() => {
    if (skipInitialRef.current) {
      skipInitialRef.current = false;
      return;
    }
    // Invalidate in-flight requests from the previous search/sort
    loadGenRef.current++;
    // Reset and reload from page 0
    isLoadingPageRef.current = false;
    setCurrentPage(0);
    setHasMorePages(true);
    if (activeViewRef.current === 'venture-mentor') {
      setVenturePages([]);
      loadVenturePage(0);
    } else {
      setMentorPages([]);
      loadMentorPage(0);
    }
  }, [ventureSearch, mentorSearch, sortBy, loadVenturePage, loadMentorPage]);

  // Reload all loaded venture pages to sync with backend after transfers
  // Uses refs to avoid recreating on every page/filter change
  const reloadVenturePages = useCallback(async () => {
    try {
      const pagesToLoad = currentPageRef.current + 1; // pages 0..currentPage
      const allData: AdvancedMatchPage[] = [];
      for (let p = 0; p < pagesToLoad; p++) {
        const data = await matchingRoundsAPI.filterAdvancedMatches(
          roundId,
          buildFilterParams(p),
        );
        allData.push(...data);
        if (data.length === 0) break;
      }
      setVenturePages(allData);
    } catch (_e) {
      // Silently fail — optimistic state is still usable
    }
  }, [roundId, buildFilterParams]);

  // Reload all loaded mentor pages to sync with backend after transfers
  // Uses refs to avoid recreating on every page/filter change
  const reloadMentorPages = useCallback(async () => {
    try {
      const pagesToLoad = currentPageRef.current + 1;
      const allData: ReverseMatchRecommendation[] = [];
      for (let p = 0; p < pagesToLoad; p++) {
        const data = await matchingRoundsAPI.filterMentors(
          roundId,
          buildFilterParams(p),
        );
        allData.push(...data);
        if (data.length === 0) break;
      }
      // Use functional setState to access the latest state (avoids stale ref
      // from React batching) and preserve mentors the API no longer returns
      // (all their ventures were removed) so their cards stay visible
      setMentorPages((prev) => {
        const returnedIds = new Set(allData.map((m) => m.mentorId));
        const emptyMentors = prev
          .filter((m) => !returnedIds.has(m.mentorId))
          .map((m) => ({ ...m, ventures: [] }));
        return [...allData, ...emptyMentors];
      });
    } catch (_e) {
      // Silently fail — optimistic state is still usable
    }
  }, [roundId, buildFilterParams]);

  const handleViewChange = useCallback(
    (view: HeatmapView) => {
      setActiveView(view);
      // Reset view-specific sort options when switching views
      const needsSortReset =
        sortByRef.current === 'unassignedVentures' ||
        sortByRef.current === 'overloadedMentors';
      if (needsSortReset) {
        setSortBy('name');
        // Eagerly sync ref so the immediate loadPage uses correct params
        sortByRef.current = 'name';
      }
      setCurrentPage(0);
      setHasMorePages(true);
      isLoadingPageRef.current = false;
      // Invalidate in-flight requests so useEffect reload (if sort changed)
      // discards rather than duplicates
      loadGenRef.current++;
      if (view === 'venture-mentor') {
        setVenturePages([]);
        loadVenturePage(0);
      } else {
        setMentorPages([]);
        loadMentorPage(0);
      }
    },
    [loadVenturePage, loadMentorPage],
  );

  // Chip hover handler
  const handleChipHover = useCallback(
    (el: HTMLElement | null, data: TooltipData | null) => {
      setTooltipAnchor(el);
      setTooltipData(data);
    },
    [],
  );

  // Remove chip handler — uses refs for rollback to avoid stale closures
  const handleRemoveChip = useCallback(
    async (chipId: string) => {
      const prevVentures = ventureDataRef.current;
      const prevMentors = mentorPagesRef.current;

      setVenturePages((pages) =>
        pages.map((p) => ({
          ...p,
          recommendations: p.recommendations.filter((r) => r.id !== chipId),
        })),
      );

      setMentorPages((pages) =>
        pages.map((m) => ({
          ...m,
          ventures: m.ventures.filter(
            (v) => `${m.mentorId}-${v.ventureId}` !== chipId,
          ),
        })),
      );

      try {
        // Resolve matchId + recommendationId for the DELETE endpoint
        let matchId = '';
        let recId = '';

        if (activeViewRef.current === 'venture-mentor') {
          // In venture view, chipId IS the recommendation ID.
          // Find the parent venture (match) that contains this recommendation.
          recId = chipId;
          for (const v of prevVentures) {
            if (v.recommendations.some((r) => r.id === chipId)) {
              matchId = v.id;
              break;
            }
          }
        } else if (prevMentors) {
          // In mentor view, chipId is `${mentorId}-${ventureId}`.
          for (const m of prevMentors) {
            const v = m.ventures.find(
              (ven) => `${m.mentorId}-${ven.ventureId}` === chipId,
            );
            if (v) {
              matchId = v.matchId;
              recId = v.recommendationId;
              break;
            }
          }
        }

        if (!matchId || !recId) {
          throw new Error('Could not resolve match/recommendation IDs');
        }

        await matchingRoundsAPI.removeRecommendation(matchId, recId);
        enqueueSnackbar('Recommendation removed', { variant: 'success' });
      } catch (e: unknown) {
        const msg = (e as AxiosError)?.response?.data?.message;
        setVenturePages(prevVentures);
        setMentorPages(prevMentors);
        enqueueSnackbar('Failed to remove recommendation', {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: msg,
            }),
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar],
  );

  // Drag start handler — track which entity is being dragged for conflict detection
  const handleDragStart = useCallback((start: DragStart) => {
    const { draggableId, source } = start;
    if (activeViewRef.current === 'venture-mentor') {
      const sourceVentureId = source.droppableId.replace('venture-', '');
      const sourceVenture = ventureDataRef.current.find(
        (v) => v.ventureId === sourceVentureId,
      );
      const chip = sourceVenture?.recommendations.find(
        (r) => r.id === draggableId,
      );
      setDraggingEntityId(chip?.mentorId ?? null);
    } else {
      // In mentor-venture view, draggableId is `${mentorId}-${ventureId}`
      const sourceMentorId = source.droppableId.replace('mentor-', '');
      const sourceMentor = mentorPagesRef.current.find(
        (m) => m.mentorId === sourceMentorId,
      );
      const chipVenture = sourceMentor?.ventures.find(
        (v) => `${sourceMentorId}-${v.ventureId}` === draggableId,
      );
      setDraggingEntityId(chipVenture?.ventureId ?? null);
    }
  }, []);

  // Drag end handler — uses refs for rollback to avoid stale closures
  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      setDraggingEntityId(null);
      const { source, destination, draggableId } = result;
      if (!destination) return;
      if (source.droppableId === destination.droppableId) return;

      const currentVentures = ventureDataRef.current;
      const currentMentors = mentorPagesRef.current;

      if (activeViewRef.current === 'venture-mentor') {
        const prevVentures = currentVentures;
        const sourceVentureId = source.droppableId.replace('venture-', '');
        const targetVentureId = destination.droppableId.replace('venture-', '');

        const sourceVenture = currentVentures.find(
          (v) => v.ventureId === sourceVentureId,
        );
        const targetVenture = currentVentures.find(
          (v) => v.ventureId === targetVentureId,
        );
        const chip = sourceVenture?.recommendations.find(
          (r) => r.id === draggableId,
        );
        if (!chip || !sourceVenture || !targetVenture) return;

        // Prevent moving a mentor to a venture that already has them
        const alreadyInTarget = targetVenture.recommendations.some(
          (r) => r.mentorId === chip.mentorId,
        );
        if (alreadyInTarget) {
          enqueueSnackbar(
            `${chip.mentorName} is already recommended for ${targetVenture.ventureName}`,
            { variant: 'warning' },
          );
          return;
        }

        setVenturePages((pages) =>
          pages.map((p) => {
            if (p.ventureId === sourceVentureId) {
              return {
                ...p,
                recommendations: p.recommendations.filter(
                  (r) => r.id !== draggableId,
                ),
              };
            }
            if (p.ventureId === targetVentureId) {
              return {
                ...p,
                recommendations: [
                  ...p.recommendations,
                  { ...chip, assigned: false },
                ],
              };
            }
            return p;
          }),
        );

        try {
          await matchingRoundsAPI.transferRecommendation({
            recommendationId: chip.id,
            sourceMatchId: sourceVenture.id,
            destinationMatchId: targetVenture.id,
          });
          const targetName =
            currentVentures.find((v) => v.ventureId === targetVentureId)
              ?.ventureName ?? 'target';
          enqueueSnackbar(`${chip.mentorName} moved to ${targetName}`, {
            variant: 'success',
          });
          // Reload to get fresh IDs from backend
          reloadVenturePages();
        } catch (e: unknown) {
          const msg = (e as AxiosError)?.response?.data?.message;
          setVenturePages(prevVentures);
          enqueueSnackbar('Failed to transfer recommendation', {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: msg,
              }),
            variant: 'error',
          });
        }
      } else {
        const prevMentors = currentMentors;
        const sourceMentorId = source.droppableId.replace('mentor-', '');
        const targetMentorId = destination.droppableId.replace('mentor-', '');

        const sourceMentor = currentMentors.find(
          (m) => m.mentorId === sourceMentorId,
        );
        const chipVenture = sourceMentor?.ventures.find(
          (v) => `${sourceMentorId}-${v.ventureId}` === draggableId,
        );
        if (!chipVenture) return;

        const targetMentor = currentMentors.find(
          (m) => m.mentorId === targetMentorId,
        );

        // Prevent moving a venture to a mentor that already has it
        const alreadyInTarget = targetMentor?.ventures.some(
          (v) => v.ventureId === chipVenture.ventureId,
        );
        if (alreadyInTarget) {
          enqueueSnackbar(
            `${chipVenture.ventureName} is already associated with ${targetMentor?.mentorName}`,
            { variant: 'warning' },
          );
          return;
        }

        // Optimistic update
        setMentorPages((pages) =>
          pages.map((m) => {
            if (m.mentorId === sourceMentorId) {
              return {
                ...m,
                ventures: m.ventures.filter(
                  (v) => `${sourceMentorId}-${v.ventureId}` !== draggableId,
                ),
              };
            }
            if (m.mentorId === targetMentorId) {
              return {
                ...m,
                ventures: [...m.ventures, { ...chipVenture, assigned: false }],
              };
            }
            return m;
          }),
        );

        try {
          // Remove old recommendation (source mentor from this venture's match)
          await matchingRoundsAPI.removeRecommendation(
            chipVenture.matchId,
            chipVenture.recommendationId,
          );
          // Create new recommendation (target mentor on the same match)
          await matchingRoundsAPI.createRecommendation(chipVenture.matchId, {
            mentorId: targetMentorId as MentorId,
          });
          const targetName =
            currentMentors.find((m) => m.mentorId === targetMentorId)
              ?.mentorName ?? 'target';
          enqueueSnackbar(`${chipVenture.ventureName} moved to ${targetName}`, {
            variant: 'success',
          });
          // Reload to get fresh IDs from backend
          reloadMentorPages();
        } catch (e: unknown) {
          const msg = (e as AxiosError)?.response?.data?.message;
          setMentorPages(prevMentors);
          enqueueSnackbar('Failed to reassign recommendation', {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: msg,
              }),
            variant: 'error',
          });
        }
      }
    },
    [enqueueSnackbar, reloadVenturePages, reloadMentorPages],
  );

  // Retry load — respects which view is active
  const handleRetry = useCallback(() => {
    setCurrentPage(0);
    setHasMorePages(true);
    setIsInitialLoading(true);
    setLoadError(false);
    isLoadingPageRef.current = false;
    if (activeViewRef.current === 'venture-mentor') {
      setVenturePages([]);
      loadVenturePage(0);
    } else {
      setMentorPages([]);
      loadMentorPage(0);
    }
  }, [loadVenturePage, loadMentorPage]);

  // ---- Computed data for Venture → Mentor view (memoized chip data) ----

  const ventureCardData = useMemo(() => {
    const cards = venturePages.map((venture) => {
      const approvedChips: ChipData[] = [];
      const assignedChips: ChipData[] = [];
      const candidateChips: ChipData[] = [];
      for (const r of venture.recommendations) {
        if (isApproved(r)) {
          approvedChips.push(recToChip(r, true));
        } else if (r.assigned) {
          assignedChips.push(recToChip(r));
        } else {
          candidateChips.push(recToChip(r));
        }
      }
      return { venture, approvedChips, assignedChips, candidateChips };
    });
    if (sortBy === 'assignedCount') {
      cards.sort((a, b) => {
        const aTotal = a.venture.recommendations.length;
        const bTotal = b.venture.recommendations.length;
        return aTotal !== bTotal
          ? bTotal - aTotal
          : a.venture.ventureName.localeCompare(b.venture.ventureName);
      });
    }
    return cards;
  }, [venturePages, sortBy]);

  // ---- Computed data for Mentor → Venture view (memoized chip data) ----

  const mentorCardData = useMemo(() => {
    if (mentorPages.length === 0) return [];

    const cards = mentorPages.map((mentor) => {
      const approvedChips: ChipData[] = [];
      const assignedChips: ChipData[] = [];
      const candidateChips: ChipData[] = [];
      for (const v of mentor.ventures) {
        if (isApproved(v)) {
          approvedChips.push(
            ventureToChip(mentor.mentorId, v, v.assigned, true),
          );
        } else if (v.assigned) {
          assignedChips.push(ventureToChip(mentor.mentorId, v, true));
        } else {
          candidateChips.push(ventureToChip(mentor.mentorId, v, false));
        }
      }
      return { mentor, approvedChips, assignedChips, candidateChips };
    });
    if (sortBy === 'assignedCount') {
      cards.sort((a, b) => {
        const aTotal = a.mentor.ventures.length;
        const bTotal = b.mentor.ventures.length;
        return aTotal !== bTotal
          ? bTotal - aTotal
          : a.mentor.mentorName.localeCompare(b.mentor.mentorName);
      });
    }
    return cards;
  }, [mentorPages, sortBy]);

  // Sync sort/filter/view to URL whenever they change
  useEffect(() => {
    const qs = stringifyQuery({
      ...(activeView !== 'venture-mentor' ? { view: activeView } : {}),
      ...(sortBy !== 'name' ? { sort: sortBy } : {}),
      ...(ventureSearch ? { vs: ventureSearch } : {}),
      ...(mentorSearch ? { ms: mentorSearch } : {}),
    });
    history.replace({
      pathname: location.pathname,
      search: qs ? `?${qs}` : '',
    });
    // Only run when filter/sort values actually change, not on every location change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, sortBy, ventureSearch, mentorSearch]);

  const handleClearAll = useCallback(() => {
    setVentureSearch('');
    setMentorSearch('');
    setSortBy('name');
  }, []);

  // Copy vote link handler for both views
  const handleCopyVentureVoteLink = useCallback(
    async (ventureId: string, matchId: string) => {
      if (!tenantId) return;
      const key = btoa(`${tenantId}|${roundId}|${matchId}`);
      const url = `${window.location.origin}/founder-match-approve?key=${key}`;
      try {
        await navigator.clipboard.writeText(url);
        setCopiedVoteLink(ventureId);
      } catch (_e) {
        enqueueSnackbar('Failed to copy link to clipboard', {
          variant: 'error',
        });
      }
    },
    [tenantId, roundId, enqueueSnackbar],
  );

  const handleCopyMentorVoteLink = useCallback(
    async (mentorId: string) => {
      if (!tenantId) return;
      const key = btoa(`${tenantId}|${roundId}|${mentorId}`);
      const url = `${window.location.origin}/mentor-match-approve?key=${key}`;
      try {
        await navigator.clipboard.writeText(url);
        setCopiedVoteLink(mentorId);
      } catch (_e) {
        enqueueSnackbar('Failed to copy link to clipboard', {
          variant: 'error',
        });
      }
    },
    [tenantId, roundId, enqueueSnackbar],
  );

  // ---- Assignment modal helpers ----

  // Maps ventureId → matchId for resolving which match to POST to.
  // Built from both venture-view data and mentor-view data for full coverage.
  const ventureMatchMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of venturePages) {
      map.set(v.ventureId, v.id);
    }
    for (const m of mentorPages) {
      for (const v of m.ventures) {
        if (v.matchId && !map.has(v.ventureId)) {
          map.set(v.ventureId, v.matchId);
        }
      }
    }
    return map;
  }, [venturePages, mentorPages]);

  const handleOpenAssignMentor = useCallback((venture: AdvancedMatchPage) => {
    setAssignModalVenture({
      ventureId: venture.ventureId,
      ventureName: venture.ventureName,
      matchId: venture.id,
      existingMentorIds: venture.recommendations.map((r) => r.mentorId),
    });
  }, []);

  const handleOpenAssignVenture = useCallback(
    (mentor: ReverseMatchRecommendation) => {
      setAssignModalMentor({
        mentorId: mentor.mentorId,
        mentorName: mentor.mentorName,
        existingVentureIds: mentor.ventures.map((v) => v.ventureId),
      });
    },
    [],
  );

  const handleCloseAssignModal = useCallback(() => {
    setAssignModalVenture(null);
    setAssignModalMentor(null);
  }, []);

  // Remove venture from round handlers
  const handleOpenRemoveFromRound = useCallback(
    (ventureId: VentureId, ventureName: string) => {
      setRemoveFromRoundVenture({ ventureId, ventureName });
    },
    [],
  );

  const handleCancelRemoveFromRound = useCallback(() => {
    setRemoveFromRoundVenture(null);
  }, []);

  const handleConfirmRemoveFromRound = useCallback(async () => {
    const target = removeFromRoundRef.current;
    if (!target) return;
    const { ventureId, ventureName } = target;
    const prevVentures = ventureDataRef.current;

    // Optimistic removal
    setVenturePages((pages) => pages.filter((v) => v.ventureId !== ventureId));
    // Invalidate stale mentor data so hasRecommendations recalculates correctly
    setMentorPages([]);
    setRemoveFromRoundVenture(null);

    try {
      await matchingRoundsAPI.removeVentures(roundId, [ventureId]);
      reloadVenturePages();
      enqueueSnackbar(`${ventureName} removed from the round`, {
        variant: 'success',
      });
    } catch (e: unknown) {
      const msg = (e as AxiosError)?.response?.data?.message;
      setVenturePages(prevVentures);
      enqueueSnackbar('Failed to remove venture from round', {
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: msg,
          }),
        variant: 'error',
      });
    }
  }, [roundId, enqueueSnackbar, reloadVenturePages]);

  // Remove mentor from round handlers
  const handleOpenRemoveFromRoundMentor = useCallback(
    (mentorId: MentorId, mentorName: string) => {
      setRemoveFromRoundMentor({ mentorId, mentorName });
    },
    [],
  );

  const handleCancelRemoveFromRoundMentor = useCallback(() => {
    setRemoveFromRoundMentor(null);
  }, []);

  const handleConfirmRemoveFromRoundMentor = useCallback(async () => {
    const target = removeFromRoundMentorRef.current;
    if (!target) return;
    const { mentorId, mentorName } = target;
    const prevMentors = mentorPagesRef.current;

    // Find mentor and collect all (matchId, recommendationId) pairs
    const mentor = prevMentors.find((m) => m.mentorId === mentorId);
    if (!mentor) return;

    const pairs = mentor.ventures.map((v) => ({
      matchId: v.matchId,
      recommendationId: v.recommendationId,
    }));

    // Optimistic removal
    setMentorPages((pages) => pages.filter((m) => m.mentorId !== mentorId));
    // Invalidate stale venture data so hasRecommendations recalculates correctly
    setVenturePages([]);
    setRemoveFromRoundMentor(null);

    if (pairs.length === 0) return;

    try {
      // Remove all recommendations for this mentor
      await Promise.all(
        pairs.map((p) =>
          matchingRoundsAPI.removeRecommendation(p.matchId, p.recommendationId),
        ),
      );
      reloadMentorPages();
      enqueueSnackbar(`${mentorName} removed from the round`, {
        variant: 'success',
      });
    } catch (e: unknown) {
      const msg = (e as AxiosError)?.response?.data?.message;
      setMentorPages(prevMentors);
      enqueueSnackbar('Failed to remove from round', {
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: msg,
          }),
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, reloadMentorPages]);

  // Optimistic update: add a new recommendation to the correct venture's card
  const handleMentorRecommendationCreated = useCallback(
    (rec: MatchRecommendation) => {
      if (!assignModalVenture) return;
      const targetVentureId = assignModalVenture.ventureId;

      setVenturePages((prev) =>
        prev.map((v) =>
          v.ventureId === targetVentureId
            ? { ...v, recommendations: [...v.recommendations, rec] }
            : v,
        ),
      );

      enqueueSnackbar(
        `${rec.mentorName} added to ${assignModalVenture.ventureName}`,
        { variant: 'success' },
      );
    },
    [assignModalVenture, enqueueSnackbar],
  );

  // Optimistic update: add a new venture entry to the correct mentor's card
  const handleVentureRecommendationCreated = useCallback(
    (ventureId: VentureId, rec: MatchRecommendation) => {
      if (!assignModalMentor) return;
      const targetMentorId = assignModalMentor.mentorId;

      // Find the venture data from venturePages to enrich the ReverseMatchVenture
      const ventureData = venturePages.find((v) => v.ventureId === ventureId);

      const newVenture: ReverseMatchVenture = {
        matchId: ventureMatchMap.get(ventureId) || '',
        recommendationId: rec.id,
        ventureId,
        ventureName: ventureData?.ventureName || '',
        stage: ventureData?.stage || '',
        description: ventureData?.description || '',
        score: rec.score,
        assigned: false,
      };

      setMentorPages((prev) =>
        prev.map((m) =>
          m.mentorId === targetMentorId
            ? { ...m, ventures: [...m.ventures, newVenture] }
            : m,
        ),
      );

      enqueueSnackbar(
        `${ventureData?.ventureName || 'Venture'} added to ${
          assignModalMentor.mentorName
        }`,
        { variant: 'success' },
      );
    },
    [assignModalMentor, venturePages, ventureMatchMap, enqueueSnackbar],
  );

  // ---- Mobile guard ----

  if (isMobile()) {
    return (
      <div className={classes.mobileMessage}>
        <Typography variant='body1' color='textSecondary'>
          Heatmap view is available on desktop only.
        </Typography>
      </div>
    );
  }

  // ---- Render ----

  const activeData =
    activeView === 'venture-mentor' ? venturePages : mentorPages;

  const isVentureView = activeView === 'venture-mentor';

  const showClearAll = !!ventureSearch || !!mentorSearch || sortBy !== 'name';

  // Centered spinner when current view has no data yet and is loading
  const showCenteredSpinner =
    isInitialLoading ||
    (isLoadingPage &&
      ((isVentureView && venturePages.length === 0) ||
        (!isVentureView && mentorPages.length === 0)));

  // Bottom spinner when current view has data and more is loading
  const showBottomSpinner =
    isLoadingPage &&
    ((isVentureView && venturePages.length > 0) ||
      (!isVentureView && mentorPages.length > 0));

  return (
    <div className={classes.container} data-testid='round-heatmap-page'>
      <HeatmapHeader
        activeView={activeView}
        onViewChange={handleViewChange}
        primarySearch={{
          placeholder: isVentureView
            ? 'Search ventures...'
            : `Search ${rb('mentors')}...`,
          value: isVentureView ? ventureSearch : mentorSearch,
          onChange: isVentureView ? setVentureSearch : setMentorSearch,
        }}
        secondarySearch={{
          placeholder: isVentureView
            ? `Search ${rb('mentors')}...`
            : 'Search ventures...',
          value: isVentureView ? mentorSearch : ventureSearch,
          onChange: isVentureView ? setMentorSearch : setVentureSearch,
        }}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onClearAll={handleClearAll}
        showClearAll={showClearAll}
      />

      <div className={classes.mainArea}>
        <div className={classes.contentColumn}>
          <DragDropContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div
              className={classes.cardList}
              ref={cardListRef}
              data-testid='heatmap-card-list'
            >
              {showCenteredSpinner ? (
                <div className={classes.listLoader}>
                  <CircularProgress />
                </div>
              ) : loadError && activeData.length === 0 ? (
                <div className={classes.emptyState}>
                  <div style={{ textAlign: 'center' }}>
                    <ErrorOutlineIcon
                      color='error'
                      style={{ fontSize: 48, marginBottom: 8 }}
                    />
                    <Typography variant='body1' color='error' gutterBottom>
                      Failed to load matching data
                    </Typography>
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={handleRetry}
                      className={classes.retryButton}
                      data-testid='heatmap-retry'
                    >
                      Retry
                    </Button>
                  </div>
                </div>
              ) : activeData.length === 0 && !isLoadingPage ? (
                <div className={classes.emptyState}>
                  <Typography variant='body1' color='textSecondary'>
                    {ventureSearch || mentorSearch ? (
                      'No results found for the current search.'
                    ) : sortBy === 'unassignedVentures' ? (
                      'No unassigned ventures found.'
                    ) : sortBy === 'overloadedMentors' ? (
                      `No overloaded ${rb('mentors')} found.`
                    ) : (
                      <>
                        No matching data. Adjust group settings at{' '}
                        <Link
                          to={getRoutePath(Pages.MATCHING_ROUND_SUBGROUPS, {
                            roundId,
                          })}
                        >
                          Subgroups
                        </Link>{' '}
                        tab.
                      </>
                    )}
                  </Typography>
                </div>
              ) : (
                <>
                  {isVentureView
                    ? ventureCardData.map(
                        (
                          {
                            venture,
                            approvedChips,
                            assignedChips,
                            candidateChips,
                          },
                          idx,
                        ) => {
                          const hasConflict =
                            !!draggingEntityId &&
                            venture.recommendations.some(
                              (r) => r.mentorId === draggingEntityId,
                            );
                          return (
                            <HeatmapCard
                              key={venture.ventureId}
                              index={idx}
                              entityName={venture.ventureName}
                              badges={[
                                { label: venture.stage, color: 'green' },
                              ]}
                              approvedChips={approvedChips}
                              approvedLabel='Approved'
                              assignedChips={assignedChips}
                              candidateChips={candidateChips}
                              droppableId={`venture-${venture.ventureId}`}
                              onRemoveChip={handleRemoveChip}
                              onChipHover={handleChipHover}
                              assignedLabel='Assigned Mentors'
                              candidateLabel='Candidates'
                              onAddClick={() => handleOpenAssignMentor(venture)}
                              addButtonLabel={`+ ${rb('mentor-u')}`}
                              onSecondaryClick={() =>
                                handleOpenRemoveFromRound(
                                  venture.ventureId,
                                  venture.ventureName,
                                )
                              }
                              secondaryButtonLabel='Remove from Round'
                              hideChipScores
                              isDropDisabled={hasConflict}
                              noItemsLabel={`No ${rb('mentors-u')} Assigned`}
                              nameTooltipData={{
                                type: 'venture',
                                name: venture.ventureName,
                                entityId: venture.ventureId,
                                fields: [
                                  {
                                    label: 'Stage',
                                    value: venture.stage || '',
                                  },
                                  {
                                    label: 'Description',
                                    value: venture.description || '',
                                  },
                                ],
                              }}
                              voteLinkCopied={
                                copiedVoteLink === venture.ventureId
                              }
                              onCopyVoteLink={() =>
                                handleCopyVentureVoteLink(
                                  venture.ventureId,
                                  venture.id,
                                )
                              }
                              isFulfilled={!!venture.fulfilled}
                            />
                          );
                        },
                      )
                    : mentorCardData.map(
                        (
                          {
                            mentor,
                            approvedChips,
                            assignedChips,
                            candidateChips,
                          },
                          idx,
                        ) => {
                          const hasConflict =
                            !!draggingEntityId &&
                            mentor.ventures.some(
                              (v) => v.ventureId === draggingEntityId,
                            );
                          return (
                            <HeatmapCard
                              key={mentor.mentorId}
                              index={idx}
                              entityName={mentor.mentorName}
                              badges={[
                                {
                                  label: mentor.specialization,
                                  color: 'green',
                                },
                              ]}
                              approvedChips={approvedChips}
                              approvedLabel='Approved'
                              assignedChips={assignedChips}
                              candidateChips={candidateChips}
                              droppableId={`mentor-${mentor.mentorId}`}
                              onRemoveChip={handleRemoveChip}
                              onChipHover={handleChipHover}
                              assignedLabel='Assigned Ventures'
                              candidateLabel='Candidates'
                              onAddClick={() => handleOpenAssignVenture(mentor)}
                              addButtonLabel='+ Venture'
                              onSecondaryClick={() =>
                                handleOpenRemoveFromRoundMentor(
                                  mentor.mentorId,
                                  mentor.mentorName,
                                )
                              }
                              secondaryButtonLabel='Remove from Round'
                              hideChipScores
                              isDropDisabled={hasConflict}
                              noItemsLabel='No Ventures Assigned'
                              nameTooltipData={{
                                type: 'mentor',
                                name: mentor.mentorName,
                                entityId: mentor.mentorId,
                                fields: [
                                  {
                                    label: 'Specialization',
                                    value: mentor.specialization || '',
                                  },
                                  { label: 'About', value: '' },
                                ],
                              }}
                              voteLinkCopied={
                                copiedVoteLink === mentor.mentorId
                              }
                              onCopyVoteLink={() =>
                                handleCopyMentorVoteLink(mentor.mentorId)
                              }
                            />
                          );
                        },
                      )}

                  {showBottomSpinner && (
                    <div className={classes.loader}>
                      <CircularProgress size={24} />
                    </div>
                  )}
                </>
              )}
            </div>
          </DragDropContext>
        </div>

        <HeatmapSidebar viewType={activeView} />
      </div>

      <HeatmapTooltip anchorEl={tooltipAnchor} data={tooltipData} />

      {assignModalVenture && (
        <HeatmapAssignMentorModal
          open
          onClose={handleCloseAssignModal}
          ventureId={assignModalVenture.ventureId}
          ventureName={assignModalVenture.ventureName}
          matchId={assignModalVenture.matchId}
          existingMentorIds={assignModalVenture.existingMentorIds}
          onRecommendationCreated={handleMentorRecommendationCreated}
        />
      )}

      {assignModalMentor && (
        <HeatmapAssignVentureModal
          open
          onClose={handleCloseAssignModal}
          mentorId={assignModalMentor.mentorId}
          mentorName={assignModalMentor.mentorName}
          existingVentureIds={assignModalMentor.existingVentureIds}
          roundVentures={venturePages}
          ventureMatchMap={ventureMatchMap}
          onRecommendationCreated={handleVentureRecommendationCreated}
        />
      )}

      <ConfirmDialog
        isOpen={removeFromRoundVenture !== null}
        title='Remove from Round'
        body='The venture will be removed from the current Matching Round. Are you sure?'
        onCancel={handleCancelRemoveFromRound}
        onSuccess={handleConfirmRemoveFromRound}
        cancelProps={{ label: 'No', variant: 'outlined' }}
        successProps={{ label: 'Yes', variant: 'contained' }}
      />

      <ConfirmDialog
        isOpen={removeFromRoundMentor !== null}
        title='Remove from Round'
        body={`All assignments for this ${rb(
          'mentor',
        )} will be removed from the current Matching Round. Are you sure?`}
        onCancel={handleCancelRemoveFromRoundMentor}
        onSuccess={handleConfirmRemoveFromRoundMentor}
        cancelProps={{ label: 'No', variant: 'outlined' }}
        successProps={{ label: 'Yes', variant: 'contained' }}
      />
    </div>
  );
});

export default MatchingRoundHeatmapPage;
